import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ROLE_LABELS, URGENCY_LABELS, CATEGORY_LABELS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ComposeDialog from "@/components/ComposeDialog";
import UrgencyBadge from "@/components/UrgencyBadge";
import CategoryBadge from "@/components/CategoryBadge";
import RequestStatusBadge from "@/components/RequestStatusBadge";
import MessageStatus, { computeStatus } from "@/components/MessageStatus";
import { Plus, Search, Send, ArrowLeft, User, CheckCircle2, XCircle, Ban, Play, Clock, Paperclip, Download, Trash2, FileText, Image as ImageIcon } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";
import AttachmentLightbox from "@/components/AttachmentLightbox";
import RefertoDialog from "@/components/RefertoDialog";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function patientLabel(t) {
  if (t?.patient?.first_name || t?.patient?.last_name) {
    return `${t.patient.first_name || ""} ${t.patient.last_name || ""}`.trim();
  }
  return t?.patient_name || "—";
}

const EVENT_LABEL = {
  created: "Richiesta creata",
  received: "Ricevuta",
  taken_in_charge: "Presa in carico",
  in_progress: "In lavorazione",
  completed: "Completata",
  rejected: "Rifiutata",
  cancelled: "Annullata",
  message: "Messaggio",
  absence_redirect: "Reindirizzata al sostituto",
};

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentPreview({ threadId, attachment, onOpen }) {
  const [src, setSrc] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const isImage = attachment.content_type?.startsWith("image/");
  const isPdf = attachment.content_type === "application/pdf";
  const previewable = isImage || isPdf;

  React.useEffect(() => {
    if (!previewable) return;
    let cancelled = false;
    let url;
    (async () => {
      try {
        const resp = await api.get(`/threads/${threadId}/attachments/${attachment.id}/thumbnail`,
                                    { responseType: "blob" });
        url = URL.createObjectURL(resp.data);
        if (!cancelled) setSrc(url);
      } catch { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [threadId, attachment.id, previewable]);

  const clickable = previewable && onOpen;
  const commonClass = "h-16 w-16 rounded-md flex-shrink-0";
  const cursorClass = clickable ? "cursor-zoom-in hover:ring-2 hover:ring-[#10B981]/60 transition" : "";

  if (!previewable) {
    return (
      <button type="button" onClick={onOpen}
              data-testid={`attachment-open-${attachment.id}`}
              className={`${commonClass} bg-[#DBEAFE] text-[#1E3A8A] flex items-center justify-center ${onOpen ? "cursor-pointer hover:ring-2 hover:ring-[#10B981]/60 transition" : ""}`}>
        <FileText className="h-6 w-6" />
      </button>
    );
  }
  if (failed) {
    return <div className={`${commonClass} bg-slate-100 text-slate-400 flex items-center justify-center text-[9px] px-1 text-center`}>Anteprima non disp.</div>;
  }
  if (!src) {
    return <div className={`${commonClass} bg-slate-100 animate-pulse`} />;
  }
  return (
    <img src={src} alt=""
         onClick={clickable ? onOpen : undefined}
         data-testid={`attachment-preview-${attachment.id}`}
         className={`${commonClass} object-cover border border-slate-200 bg-slate-50 ${cursorClass}`} />
  );
}

function AttachmentsPanel({ threadId, attachments, currentUserId, onDownload, onRemove, onPick, onOpenLightbox, busy }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white backdrop-blur" data-testid="attachments-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1E3A8A]/80 flex items-center gap-1.5">
          <Paperclip className="h-3 w-3" /> Allegati ({attachments.length})
        </div>
        <Button size="sm" variant="outline" onClick={onPick} disabled={busy}
                data-testid="attachment-upload-btn"
                className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#1E3A8A]">
          {busy ? <SynapseLoader size="sm" inline className="mr-1.5" /> : <Paperclip className="h-3.5 w-3.5 mr-1.5" />}
          {busy ? "Caricamento…" : "Aggiungi allegato"}
        </Button>
      </div>
      {attachments.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-1">
          Nessun allegato. Puoi caricare PDF o immagini (max 10 MB).
        </div>
      ) : (
        <div className="space-y-1.5">
          {attachments.map((a, i) => {
            const openLb = onOpenLightbox ? () => onOpenLightbox(i) : undefined;
            return (
              <div key={a.id}
                   data-testid={`attachment-${a.id}`}
                   className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-white border border-slate-100">
                <AttachmentPreview threadId={threadId} attachment={a} onOpen={openLb} />
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={openLb}
                          data-testid={`attachment-filename-${a.id}`}
                          className={`text-sm font-medium text-slate-800 truncate block text-left w-full ${openLb ? "hover:text-[#1E3A8A] hover:underline" : ""}`}>
                    {a.filename}
                  </button>
                  <div className="text-[11px] text-slate-500">
                    {formatBytes(a.size)} · {a.uploader_name} · {new Date(a.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => onDownload(a)}
                        data-testid={`attachment-download-${a.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-[#10B981] hover:bg-emerald-50" title="Scarica">
                  <Download className="h-4 w-4" />
                </Button>
                {a.uploader_id === currentUserId && (
                  <Button size="icon" variant="ghost" onClick={() => onRemove(a)}
                          data-testid={`attachment-delete-${a.id}`}
                          className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50" title="Rimuovi">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkflowActions({ selected, user, onChange, busy }) {
  const isCreator = selected?.created_by === user?.id;
  const st = selected?.request_status || "inviata";
  if (["completata", "rifiutata", "annullata"].includes(st)) {
    return (
      <div className="mt-3 text-[11px] text-slate-500 italic">
        Questa richiesta è chiusa. Rimane nello storico.
      </div>
    );
  }
  if (isCreator) {
    return (
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" disabled={busy}
                onClick={() => onChange("annullata")}
                data-testid="wf-annulla"
                className="border-red-200 text-red-700 hover:bg-red-50">
          <Ban className="h-4 w-4 mr-1" /> Annulla richiesta
        </Button>
      </div>
    );
  }
  // recipient
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {["inviata", "ricevuta"].includes(st) && (
        <Button size="sm" disabled={busy} onClick={() => onChange("presa_in_carico")}
                data-testid="wf-take"
                className="bg-[#1E3A8A] hover:bg-[#1E40AF]">
          <Play className="h-4 w-4 mr-1" /> Prendi in carico
        </Button>
      )}
      {st === "presa_in_carico" && (
        <Button size="sm" variant="outline" disabled={busy}
                onClick={() => onChange("in_lavorazione")} data-testid="wf-progress">
          <Clock className="h-4 w-4 mr-1" /> Segna in lavorazione
        </Button>
      )}
      {["presa_in_carico", "in_lavorazione"].includes(st) && (
        <Button size="sm" disabled={busy} onClick={() => onChange("completata")}
                data-testid="wf-complete"
                className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle2 className="h-4 w-4 mr-1" /> Completa
        </Button>
      )}
      {["ricevuta", "presa_in_carico"].includes(st) && (
        <Button size="sm" variant="outline" disabled={busy}
                onClick={() => onChange("rifiutata")} data-testid="wf-reject"
                className="border-red-200 text-red-700 hover:bg-red-50">
          <XCircle className="h-4 w-4 mr-1" /> Rifiuta
        </Button>
      )}
    </div>
  );
}

function Timeline({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white mb-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1E3A8A]/80 mb-2">
        Cronologia richiesta
      </div>
      <div className="space-y-1.5">
        {history.map((h) => (
          <div key={h.id} className="text-xs flex items-start gap-2" data-testid={`timeline-${h.event}`}>
            <span className="text-slate-500 font-mono flex-shrink-0 pt-0.5">
              {new Date(h.at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </span>
            <span className="text-slate-700">
              <span className="font-semibold">{EVENT_LABEL[h.event] || h.event}</span>
              {h.actor_name ? ` · ${h.actor_name}` : ""}
              {h.note ? <span className="text-slate-500 italic"> — {h.note}</span> : null}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const { user } = useAuth();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get("filter") || "attive";
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attBusy, setAttBusy] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const attFileRef = useRef(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialFilter); // attive|completata|all|da_gestire|urgenti|inviate
  const [replyBody, setReplyBody] = useState("");
  const [replyCategory, setReplyCategory] = useState("generica");
  const [replyUrgency, setReplyUrgency] = useState("normale");
  const [sending, setSending] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [refertoOpen, setRefertoOpen] = useState(false);
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    const { data } = await api.get("/threads");
    setThreads(data);
  };
  const loadThread = async (id) => {
    if (!id) { setSelected(null); setAttachments([]); return; }
    try {
      const { data } = await api.get(`/threads/${id}`);
      setSelected(data);
      loadAttachments(id);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Impossibile caricare la conversazione");
    }
  };

  const loadAttachments = async (id) => {
    try {
      const { data } = await api.get(`/threads/${id}/attachments`);
      setAttachments(data);
    } catch { setAttachments([]); }
  };

  const onAttachmentPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)"); return; }
    setAttBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/threads/${selected.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Allegato caricato");
      await loadAttachments(selected.id);
      await loadThread(selected.id);
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore upload");
    } finally { setAttBusy(false); if (attFileRef.current) attFileRef.current.value = ""; }
  };

  const downloadAttachment = async (att) => {
    try {
      const resp = await api.get(`/threads/${selected.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Errore download"); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Rimuovere l'allegato "${att.filename}"?`)) return;
    try {
      await api.delete(`/threads/${selected.id}/attachments/${att.id}`);
      toast.success("Allegato rimosso");
      await loadAttachments(selected.id);
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore");
    }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadThread(threadId); }, [threadId]);

  const handleOpen = (t) => { navigate(`/messaggi/${t.id}`); };
  const handleBack = () => { navigate("/messaggi"); setSelected(null); };

  const sendReply = async () => {
    if (!replyBody.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/threads/${selected.id}/messages`, {
        body: replyBody.trim(), category: replyCategory, urgency: replyUrgency,
      });
      setReplyBody(""); setReplyCategory("generica"); setReplyUrgency("normale");
      await loadThread(selected.id);
      await loadThreads();
      toast.success("Risposta inviata");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  // Keep URL query param in sync with the active status filter so links
  // like /messaggi?filter=urgenti are shareable and refresh-safe.
  useEffect(() => {
    const cur = searchParams.get("filter") || "attive";
    if (cur !== statusFilter) {
      const next = new URLSearchParams(searchParams);
      if (statusFilter === "attive") next.delete("filter");
      else next.set("filter", statusFilter);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    const cur = searchParams.get("filter") || "attive";
    if (cur !== statusFilter) setStatusFilter(cur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filteredThreads = threads.filter((t) => {
    const s = `${t.subject} ${t.patient_name} ${t.patient_ref}`.toLowerCase();
    const textOk = !filter || s.includes(filter.toLowerCase());
    const st = t.request_status || "inviata";
    let statusOk = true;
    if (statusFilter === "attive") statusOk = !["completata", "rifiutata", "annullata"].includes(st);
    else if (statusFilter === "completata") statusOk = st === "completata";
    else if (statusFilter === "all") statusOk = true;
    else if (statusFilter === "da_gestire") {
      // Richieste dirette a me (non da me), ancora non completate/rifiutate
      statusOk = t.created_by !== user?.id &&
        ["inviata", "ricevuta", "presa_in_carico", "in_lavorazione"].includes(st);
    } else if (statusFilter === "urgenti") {
      statusOk = ["alta", "critica"].includes(t.priority) &&
        !["completata", "rifiutata", "annullata"].includes(st);
    } else if (statusFilter === "inviate") {
      statusOk = t.created_by === user?.id;
    }
    return textOk && statusOk;
  });

  const changeStatus = async (newStatus, note = "") => {
    if (!selected) return;
    setStatusBusy(true);
    try {
      const { data } = await api.patch(`/threads/${selected.id}/status`, { new_status: newStatus, note });
      toast.success(`Stato aggiornato: ${newStatus.replace(/_/g, " ")}`);
      await loadThread(selected.id);
      await loadThreads();
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento");
    } finally { setStatusBusy(false); }
  };

  const otherParticipants = (t) =>
    (t.participants_info || []).filter((p) => p.id !== user?.id);

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 md:p-6 border-b border-slate-100 bg-white flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#1E3A8A]/80">Comunicazioni</p>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Messaggi</h1>
        </div>
        <Button onClick={() => setComposeOpen(true)} data-testid="new-message-btn"
                className="h-11 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                           hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                           text-white font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Nuova
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 min-h-0">
        {/* List */}
        <div className={`lg:col-span-4 border-r border-slate-100 bg-white flex flex-col min-h-0 ${selected ? "hidden lg:flex" : "flex"}`}>
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                     placeholder="Cerca per paziente o oggetto…"
                     className="pl-9 bg-white border-slate-200 text-slate-800 placeholder:text-slate-400" data-testid="threads-search" />
            </div>
            <div className="flex gap-1 flex-wrap text-[11px] font-semibold">
              {[
                {k:"attive", l:"Attive"},
                {k:"da_gestire", l:"Da gestire"},
                {k:"urgenti", l:"Urgenti"},
                {k:"inviate", l:"Inviate"},
                {k:"completata", l:"Completate"},
                {k:"all", l:"Tutte"},
              ].map((f) => (
                <button key={f.k} onClick={() => setStatusFilter(f.k)}
                        data-testid={`status-filter-${f.k}`}
                        className={`px-2.5 py-1 rounded-md ${
                          statusFilter === f.k
                            ? "bg-gradient-to-r from-[#1E3A8A] to-[#10B981] text-white shadow-md shadow-emerald-500/20"
                            : "bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                        }`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="threads-list">
            {filteredThreads.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                Nessuna conversazione. Inizia con <span className="font-semibold text-slate-700">Nuova</span>.
              </div>
            )}
            {filteredThreads.map((t) => (
              <button key={t.id} onClick={() => handleOpen(t)}
                      data-testid={`thread-item-${t.id}`}
                      className={`w-full text-left p-3 md:p-4 border-b border-slate-100 hover:bg-white transition-colors ${
                        selected?.id === t.id ? "bg-white" : ""
                      }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-slate-900 truncate flex-1 text-[15px]">{t.subject}</div>
                  <span className="text-[11px] text-slate-500 flex-shrink-0 mt-0.5">{formatWhen(t.last_message_at)}</span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <RequestStatusBadge status={t.request_status || "inviata"} />
                  <span className="text-[11px] text-slate-500">
                    · {(CATEGORY_LABELS[t.request_type || t.last_category] || "").split(" /")[0]}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-1.5">
                  <span className="text-slate-500">Paz.</span> <span className="text-slate-800 font-medium">{patientLabel(t)}</span>
                  <span className="hidden sm:inline"> · {otherParticipants(t).map((p) => p.name || `${p.first_name||""} ${p.last_name||""}`.trim()).join(", ")}</span>
                </div>
                {t.unread_count > 0 && (
                  <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#10B981] text-white text-[10px] font-bold shadow-md shadow-emerald-500/20"
                       data-testid={`thread-unread-${t.id}`}>
                    {t.unread_count} da leggere
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className={`lg:col-span-8 flex flex-col min-h-0 bg-transparent ${selected ? "flex" : "hidden lg:flex"}`}>
          {!selected && (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-sm p-6 text-center">
              Seleziona una conversazione per visualizzare i messaggi.
            </div>
          )}
          {selected && (
            <>
              <div className="p-4 border-b border-slate-100 bg-white backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={handleBack}
                          data-testid="thread-back-btn" className="lg:hidden text-slate-300">
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <RequestStatusBadge status={selected.request_status || "inviata"} />
                      <CategoryBadge category={selected.request_type || "generica"} />
                    </div>
                    <h2 className="font-heading text-lg md:text-xl font-bold text-slate-900 truncate mt-1">
                      {selected.subject}
                    </h2>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Paziente: <span className="text-slate-800 font-medium">{patientLabel(selected)}</span>
                      {selected.patient?.internal_ref && <span> · Rif: {selected.patient.internal_ref}</span>}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {otherParticipants(selected).map((p) => (
                        <span key={p.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] bg-white text-slate-700 border border-slate-200">
                          <User className="h-3 w-3" />
                          {p.name || `${p.first_name||""} ${p.last_name||""}`.trim()} · {ROLE_LABELS[p.profession] || ROLE_LABELS[p.role]}
                        </span>
                      ))}
                    </div>
                    <WorkflowActions selected={selected} user={user}
                                     onChange={changeStatus} busy={statusBusy} />
                    {/* Referto: solo specialisti destinatari, con email paziente presente */}
                    {(user?.profession === "medico_specialista"
                      && selected.created_by !== user?.id
                      && (selected.patient?.email || "").trim()) && (
                      <div className="mt-2">
                        <Button size="sm"
                                data-testid="thread-referto-btn"
                                onClick={() => setRefertoOpen(true)}
                                className="h-8 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-semibold shadow-sm">
                          <FileText className="h-3.5 w-3.5 mr-1.5" />
                          Crea referto & invia al paziente
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="messages-list">
                {selected.history?.length > 0 && <Timeline history={selected.history} />}
                <AttachmentsPanel threadId={selected.id}
                                  attachments={attachments}
                                  currentUserId={user?.id}
                                  onDownload={downloadAttachment}
                                  onRemove={removeAttachment}
                                  onPick={() => attFileRef.current?.click()}
                                  onOpenLightbox={(i) => setLightboxIndex(i)}
                                  busy={attBusy} />
                <input ref={attFileRef} type="file"
                       accept="application/pdf,image/jpeg,image/png,image/webp,image/gif"
                       onChange={onAttachmentPick}
                       className="hidden" data-testid="attachment-file-input" />
                {selected.messages?.map((m) => {
                  const mine = m.sender_id === user?.id;
                  const otherIds = (selected.participants || []).filter((id) => id !== user?.id);
                  const status = mine ? computeStatus(m, user?.id, otherIds) : null;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 border ${
                        mine
                          ? "bg-gradient-to-br from-[#1E3A8A] to-[#10B981] text-white border-transparent shadow-lg shadow-emerald-500/20"
                          : "bg-white text-slate-800 border-slate-200 backdrop-blur"
                      }`} data-testid={`message-${m.id}`}>
                        <div className={`flex items-center gap-2 mb-1.5 text-[11px] ${mine ? "text-white/80" : "text-slate-500"}`}>
                          <span className="font-semibold">{m.sender?.name || "?"}</span>
                          <span>·</span>
                          <span>{formatWhen(m.created_at)}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <CategoryBadge category={m.category} />
                          <UrgencyBadge urgency={m.urgency} />
                        </div>
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
                        {mine && (
                          <div className="mt-1.5 flex justify-end">
                            <MessageStatus status={status} tone="light" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div className="border-t border-slate-100 bg-white p-4">
                <div className="grid grid-cols-2 gap-2 mb-2 [&_button[role=combobox]]:bg-white [&_button[role=combobox]]:border-slate-200 [&_button[role=combobox]]:text-slate-800">
                  <Select value={replyCategory} onValueChange={setReplyCategory}>
                    <SelectTrigger data-testid="reply-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={replyUrgency} onValueChange={setReplyUrgency}>
                    <SelectTrigger data-testid="reply-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(URGENCY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 items-end">
                  <Textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                            placeholder="Scrivi una risposta…" rows={2}
                            data-testid="reply-body"
                            className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400"
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendReply(); }} />
                  <Button onClick={sendReply} disabled={sending || !replyBody.trim()}
                          data-testid="reply-send"
                          className="h-11 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                                     hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                                     text-white shadow-lg shadow-emerald-500/20">
                    {sending ? <SynapseLoader size="sm" inline /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Suggerimento: Ctrl/Cmd + Invio per inviare.</div>
              </div>
            </>
          )}
        </div>
      </div>

      <ComposeDialog open={composeOpen} onOpenChange={setComposeOpen}
                     onCreated={(t) => { loadThreads(); navigate(`/messaggi/${t.id}`); }} />

      <AttachmentLightbox
        open={lightboxIndex >= 0 && lightboxIndex < attachments.length}
        onClose={() => setLightboxIndex(-1)}
        threadId={selected?.id}
        attachments={attachments}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
}
